import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, ArrowRight, GitCompareArrows, Loader2, Minus, Plus, RefreshCw } from 'lucide-react';
import { getAuthHeaders } from '@/context/AuthContext';
import { API_BASE } from '@/config/api';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';

const FIELD_LABEL = {
  scene_number: 'number', scene_name: 'name', location: 'location',
  interior_exterior: 'INT/EXT', time_of_day: 'time', text: 'scene text',
};

const Head = ({ s }) => (
  <span className="text-xs">
    <span className="font-mono font-semibold text-primary mr-1.5">{s.scene_number}</span>
    <span className="font-medium">{s.raw_slugline || s.scene_name || s.location || '—'}</span>
  </span>
);

/**
 * Revision re-import (M2.4). Shows what a newly parsed script would change
 * on the film and applies only what the user ticks. Defaults: update
 * changed scenes, add new ones, keep removed ones (deleting is opt-in
 * because it takes their shots with it).
 *
 * Props: filmId, scenes (the full parse), diff (from /script/diff),
 *        onApplied(result), onCancel, aiUsed
 */
export default function RevisionDiff({ filmId, scenes, diff, onApplied, onCancel, aiUsed }) {
  const changed = useMemo(() => diff.matched.filter((m) => m.changes.length), [diff]);
  const unchanged = useMemo(() => diff.matched.filter((m) => !m.changes.length), [diff]);

  const [updSel, setUpdSel] = useState(() => Object.fromEntries(changed.map((m) => [m.scene_id, true])));
  const [newSel, setNewSel] = useState(() => Object.fromEntries(diff.new.map((n) => [n.index, true])));
  const [delSel, setDelSel] = useState({});
  const [renumber, setRenumber] = useState(true);
  const [showUnchanged, setShowUnchanged] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState(null);

  const nUpd = Object.values(updSel).filter(Boolean).length;
  const nNew = Object.values(newSel).filter(Boolean).length;
  const nDel = Object.values(delSel).filter(Boolean).length;
  const nothing = nUpd + nNew + nDel === 0;

  const apply = async () => {
    setApplying(true);
    setError(null);
    try {
      const body = {
        scenes: scenes.map((s) => ({ ...s, selected: true })),
        updates: changed.filter((m) => updSel[m.scene_id]).map((m) => ({ scene_id: m.scene_id, index: m.index })),
        creates: diff.new.filter((n) => newSel[n.index]).map((n) => n.index),
        deletes: diff.removed.filter((r) => delSel[r.scene_id]).map((r) => r.scene_id),
        renumber,
      };
      const { data } = await axios.post(`${API_BASE}/films/${filmId}/script/revision`, body, { headers: getAuthHeaders() });
      if (!data.success) throw new Error(data.error || 'Could not apply the revision');
      onApplied?.(data);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Could not apply the revision');
    } finally {
      setApplying(false);
    }
  };

  const Section = ({ title, count, tone, children, hint }) => (
    <section className="rounded-lg border border-border overflow-hidden">
      <div className={cn('flex items-center gap-2 px-3 py-2 text-xs font-semibold', tone)}>
        {title} <span className="font-normal opacity-70">({count})</span>
        {hint && <span className="ml-auto font-normal text-[11px] opacity-80">{hint}</span>}
      </div>
      <div className="divide-y divide-border">{children}</div>
    </section>
  );

  return (
    <div className="space-y-4" data-testid="revision-diff">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <h4 className="text-sm font-semibold flex items-center gap-2">
          <GitCompareArrows className="size-4 text-primary" /> Compared with the scenes on this film
        </h4>
        <p className="text-xs text-muted-foreground sm:ml-auto">
          {diff.summary.changed} changed · {diff.summary.unchanged} unchanged · {diff.summary.new} new · {diff.summary.removed} removed
          {diff.summary.renumbered > 0 && ` · ${diff.summary.renumbered} renumbered`}
        </p>
      </div>

      <p className="text-xs text-muted-foreground">
        Updating a scene rewrites its heading and text only. Shots, cast, status, schedule and the elements you added by hand stay.
        {aiUsed && ' The fresh AI breakdown replaces the old AI items; your own items are kept.'}
      </p>

      {changed.length > 0 && (
        <Section title="Changed" count={changed.length} tone="bg-amber-500/10 text-amber-800 dark:text-amber-300" hint="ticked scenes are updated">
          {changed.map((m) => {
            const s = scenes[m.index];
            return (
              <label key={m.scene_id} className="flex items-start gap-3 px-3 py-2 cursor-pointer hover:bg-accent/40" data-testid={`rev-changed-${m.scene_id}`}>
                <Checkbox checked={!!updSel[m.scene_id]} onCheckedChange={(v) => setUpdSel((p) => ({ ...p, [m.scene_id]: !!v }))} className="mt-0.5" />
                <div className="min-w-0 flex-1 space-y-0.5">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <span className="text-xs text-muted-foreground line-through decoration-muted-foreground/60">
                      <span className="font-mono">{m.existing.scene_number}</span> {m.existing.scene_name || m.existing.location}
                    </span>
                    <ArrowRight className="size-3 text-muted-foreground" />
                    <Head s={s} />
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {m.changes.map((c) => (
                      <span key={c} className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-800 dark:text-amber-300">{FIELD_LABEL[c] || c}</span>
                    ))}
                    {m.matched_by !== 'number' && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-secondary text-muted-foreground" title={`Matched by ${m.matched_by} (${Math.round(m.confidence * 100)}%)`}>
                        matched by {m.matched_by}
                      </span>
                    )}
                    {(m.existing.shot_count > 0 || m.existing.cast_count > 0) && (
                      <span className="text-[10px] text-muted-foreground">· keeps {m.existing.shot_count} shots, {m.existing.cast_count} cast</span>
                    )}
                  </div>
                </div>
              </label>
            );
          })}
        </Section>
      )}

      {diff.new.length > 0 && (
        <Section title="New scenes" count={diff.new.length} tone="bg-green-500/10 text-green-800 dark:text-green-300" hint="ticked scenes are added">
          {diff.new.map((n) => (
            <label key={n.index} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-accent/40" data-testid={`rev-new-${n.index}`}>
              <Checkbox checked={!!newSel[n.index]} onCheckedChange={(v) => setNewSel((p) => ({ ...p, [n.index]: !!v }))} />
              <Plus className="size-3 text-green-600" />
              <Head s={scenes[n.index]} />
            </label>
          ))}
        </Section>
      )}

      {diff.removed.length > 0 && (
        <Section title="Not in the revision" count={diff.removed.length} tone="bg-red-500/10 text-red-800 dark:text-red-300" hint="tick to delete — off by default">
          {diff.removed.map((r) => (
            <label key={r.scene_id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-accent/40" data-testid={`rev-removed-${r.scene_id}`}>
              <Checkbox checked={!!delSel[r.scene_id]} onCheckedChange={(v) => setDelSel((p) => ({ ...p, [r.scene_id]: !!v }))} />
              <Minus className="size-3 text-red-600" />
              <span className="text-xs">
                <span className="font-mono font-semibold mr-1.5">{r.scene_number}</span>{r.scene_name || r.location || '—'}
              </span>
              {(r.shot_count > 0 || r.cast_count > 0) && (
                <span className="text-[10px] text-muted-foreground inline-flex items-center gap-1 ml-auto">
                  <AlertTriangle className="size-3 text-amber-600" /> deleting drops {r.shot_count} shots
                </span>
              )}
            </label>
          ))}
        </Section>
      )}

      {unchanged.length > 0 && (
        <button type="button" onClick={() => setShowUnchanged((v) => !v)} className="text-xs text-muted-foreground hover:text-foreground">
          {showUnchanged ? 'Hide' : 'Show'} {unchanged.length} unchanged
        </button>
      )}
      {showUnchanged && (
        <ul className="text-xs text-muted-foreground grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1">
          {unchanged.map((m) => <li key={m.scene_id}><span className="font-mono">{m.existing.scene_number}</span> {m.existing.scene_name || m.existing.location}</li>)}
        </ul>
      )}

      {diff.summary.renumbered > 0 && (
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <Checkbox checked={renumber} onCheckedChange={(v) => setRenumber(!!v)} data-testid="rev-renumber" />
          Adopt the revision's scene numbers ({diff.summary.renumbered} scenes renumbered)
        </label>
      )}

      {error && (
        <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>
      )}

      <div className="flex items-center justify-between pt-2 border-t">
        <Button variant="outline" onClick={onCancel} disabled={applying}>Back</Button>
        <Button onClick={apply} disabled={applying || nothing} data-testid="rev-apply">
          {applying ? <Loader2 className="size-4 mr-2 animate-spin" /> : <RefreshCw className="size-4 mr-2" />}
          Apply revision{nothing ? '' : ` — ${[nUpd && `${nUpd} update${nUpd > 1 ? 's' : ''}`, nNew && `${nNew} new`, nDel && `${nDel} delete${nDel > 1 ? 's' : ''}`].filter(Boolean).join(', ')}`}
        </Button>
      </div>
    </div>
  );
}
